import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

const IncidentScatterPlot = ({ data }) => {
  const svgRef = useRef();

  const [dimensions, setDimensions] = useState({ width: 300, height: 300 });

  useEffect(() => {
    const handleResize = () => {
      const width = svgRef.current.parentElement.offsetWidth;
      const height = svgRef.current.parentElement.offsetHeight;
      setDimensions({ width, height });
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const parseDate = d3.timeParse("%Y-%m-%d");
    const parseTime = d3.timeParse("%H:%M");
    const processedData = data.map((d) => ({
      ...d,
      date: parseDate(d.date),
      time: parseTime(d.time),
    }));

    const margin = { top: 20, right: 30, bottom: 80, left: 50 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    // Clear previous chart
    d3.select(svgRef.current).selectAll("*").remove();

    const xScale = d3.scaleTime()
      .domain(d3.extent(processedData, (d) => d.date))
      .range([0, width]);

    const yScale = d3.scaleTime()
      .domain(d3.extent(processedData, (d) => d.time))
      .range([height, 0]);

    const colorScale = d3.scaleOrdinal()
      .domain([...new Set(processedData.map((d) => d.type))])
      .range(d3.schemeCategory10);

    const svg = d3.select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Add X axis
    const xAxis = d3
      .axisBottom(xScale)
      .ticks(d3.timeDay.every(1))
      .tickFormat(d3.timeFormat("%b %d"));
    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis)
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end");

    // Add Y axis
    const yAxis = d3.axisLeft(yScale).tickFormat(d3.timeFormat("%H:%M"));
    svg.append("g").call(yAxis);

    // Tooltip
    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "absolute")
      .style("background", "white")
      .style("border", "1px solid #ccc")
      .style("padding", "5px")
      .style("border-radius", "5px")
      .style("display", "none")
      .style("pointer-events", "none");

    // Add dots
    const circles = svg
      .selectAll("circle")
      .data(processedData)
      .enter()
      .append("circle")
      .attr("cx", (d) => xScale(d.date))
      .attr("cy", (d) => yScale(d.time))
      .attr("r", 6)
      .attr("fill", (d) => colorScale(d.type))
      .attr("opacity", 0.8)
      .attr("class", (d) => `dot-${d.type}`)
      .on("mouseover", (event, d) => {
        tooltip
          .style("display", "block")
          .html(`
            <strong>Type:</strong> ${d.type}<br>
            <strong>Date:</strong> ${d3.timeFormat("%b %d, %Y")(d.date)}<br>
            <strong>Time:</strong> ${d3.timeFormat("%H:%M")(d.time)}
          `);
      })
      .on("mousemove", (event) => {
        tooltip
          .style("top", `${event.pageY + 10}px`)
          .style("left", `${event.pageX + 10}px`);
      })
      .on("mouseout", () => {
        tooltip.style("display", "none");
      });

    // Add legend
    const legend = svg
      .append("g")
      .attr("transform", `translate(0, ${height + 40})`);

    const legendItems = legend
      .selectAll(".legend-item")
      .data(colorScale.domain())
      .enter()
      .append("g")
      .attr("class", "legend-item")
      .attr("transform", (d, i) => `translate(${i * 100}, 0)`)
      .style("cursor", "pointer")
      // .on("click", (event, type) => {
      //   const dots = d3.selectAll(`.dot-${type}`);
      //   const legendItem = d3.select(`.legend-item-${type}`);
      
      //   if (!dots.empty() && !legendItem.empty()) {
      //     const isActive = !legendItem.classed("inactive");
      //     dots.style("opacity", isActive ? 0 : 0.8);
      //     legendItem.classed("inactive", !isActive);
      //   } else {
      //     console.warn(`No elements found for type: ${type}`);
      //   }
      // });

    legendItems
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", 10)
      .attr("height", 10)
      .style("fill", colorScale)
      .attr("class", (d) => `legend-item-${d}`);

    legendItems
      .append("text")
      .attr("x", 20)
      .attr("y", 10)
      .text((d) => d)
      .style("text-anchor", "start")
      .style("alignment-baseline", "middle");

    // Cleanup function
    return () => {
      d3.select(svgRef.current).selectAll("*").remove();
      tooltip.remove();
    };
  }, [data, dimensions]);

  return <svg ref={svgRef}></svg>;
};

export default IncidentScatterPlot;
